import QueryPreviews from "./QueryPreviews";
import { Button, IconButton } from "@avenueops/web-components";
import { OvalLoader } from "@avenueops/web-components";
import { ArrowRightCircleIcon, ArrowsPointingInIcon, ArrowsPointingOutIcon, PlusCircleIcon, SparklesIcon } from "@heroicons/react/24/outline";
import { useMonaco } from "@monaco-editor/react";
import { ConnectionType, FilterMatchType, RowFilter, WorkflowTriggerType } from "@prisma/client";
import sqlFormatter from "@sqltools/formatter";
import GeneralModal from "components/GeneralModal";
import SqlEditor from "components/monaco-editor/SqlEditor";
import { MonacoEditor } from "components/monaco-editor/monaco.types";
import { useOrganization } from "context/OrganizationContext";
import { useQueryRunResults } from "context/QueryRunContext";
import _ from "lodash";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { toastError } from "utils/toast";
import { trpc, trpc2 } from "utils/trpc";

interface Props {
  prefix?: string;
  runPreview: (
    databaseId: string,
    query: string,
    queryMeta?: { includeHeaders: boolean; isAggregation: boolean },
    rowFilters?: Omit<RowFilter, "id" | "workflowId">[],
    rowFilterMatchType?: FilterMatchType,
    originalRows?: any[],
  ) => Promise<{ resultRows: any[]; dataviewQueryDurationMs: number; totalNumRows: number }>;
  previewLoading?: boolean;
  previewOpen: boolean;
  setSidebarOpen: Dispatch<SetStateAction<boolean>>;
  setPreviewOpen: Dispatch<SetStateAction<boolean>>;
  disableDataViewImport?: boolean;
  readOnly?: boolean;
  isFullScreen?: boolean;
  previewColumnsSelectable?: boolean;
  setIsFullScreen?: (_: boolean) => void;
}

export default function QueryEditor({
  prefix = "",
  setSidebarOpen,
  previewOpen,
  setPreviewOpen,
  runPreview,
  previewLoading,
  disableDataViewImport = false,
  readOnly = false,
  isFullScreen,
  setIsFullScreen,
  previewColumnsSelectable,
}: Props) {
  const fieldPrefix = prefix ? `${prefix}.` : "";
  const [editorInstance, setEditorInstance] = useState<MonacoEditor>();
  const [explanationMessage, setExplanationMessage] = useState<string>();
  const [explanationModalVisible, setExplanationModalVisible] = useState<boolean>(false);

  const organization = useOrganization();
  const { watch, setValue, control } = useFormContext();
  const monaco = useMonaco();
  const { queryRunResults, setQueryRunResults } = useQueryRunResults();

  const databaseId = watch(`${fieldPrefix}databaseId`);
  const triggerType = watch(`${fieldPrefix}workflowTriggerType`);
  const dataViewId = watch(`${fieldPrefix}dataViewId`);
  const query = watch(`${fieldPrefix}query`);
  const queryMeta = watch(`${fieldPrefix}queryMeta`);
  const rowFilters = watch(`${fieldPrefix}rowFilters`);
  const rowFilterMatchType = watch(`${fieldPrefix}rowFilterMatchType`);

  // Editor Instance values
  const currentEditorValue = query;

  const { data: connectionData } = trpc2.v2.databaseConnections.getSchema.useQuery(
    { connectionId: databaseId },
    {
      enabled: !!databaseId,
    },
  );

  const { data: dataViews, isLoading: dataViewsLoading } = trpc.dataviews.list.useQuery();
  const selectedDataView = dataViewId ? dataViews?.find((dataView) => dataView.id === dataViewId) : undefined;

  const runBaseQueryPreview = async () => {
    try {
      const { resultRows, totalNumRows, dataviewQueryDurationMs } = await runPreview(databaseId, query, queryMeta, rowFilters, rowFilterMatchType);
      if (setQueryRunResults && resultRows) {
        setQueryRunResults(resultRows, { totalNumRows, dataviewQueryDurationMs });
      }
      return resultRows;
    } catch (error: any) {
      let errMsg = "";
      if (error?.data?.friendlyMessage) {
        errMsg = error.data.friendlyMessage;
      } else {
        errMsg = error?.message ?? "Unknown Error.";
      }
      setQueryRunResults([], { error: errMsg });
      return [];
    } finally {
      setPreviewOpen(true);
    }
  };

  const explainMutation = trpc.dataviews.explain.useMutation({
    onSuccess: (data) => {
      setExplanationMessage(data);
      setExplanationModalVisible(true);
    },
    onError(error: any) {
      toastError(error, "Encountered an error when trying to explain the query.");
    },
  });

  const runExplainQueryMutation = () => {
    explainMutation.mutate({
      query: currentEditorValue ?? "",
      databaseId,
    });
  };

  const onFormatQuery = () => {
    const query = currentEditorValue;
    const formattedQuery = sqlFormatter.format(query || "");
    editorInstance?.setValue(formattedQuery);
  };

  useEffect(() => {
    if (editorInstance) {
      editorInstance.setValue(currentEditorValue ?? "");
    }
  }, [editorInstance]);

  // Made this an inline function to improve readability
  const editorReadonly = (() => {
    if (readOnly) {
      return true;
    }
    if (connectionData?.databaseType === ConnectionType.TABLEAU && (!queryMeta?.workbook?.id || !queryMeta?.view?.id)) {
      return true;
    }

    return false;
  })();
  return (
    <>
      {organization?.openAiExplainQueryEnabled || (setIsFullScreen && isFullScreen !== undefined) ? (
        <div className="absolute top-1 right-2 flex flex-row gap-1 items-center">
          {organization?.openAiExplainQueryEnabled ? (
            <IconButton
              type="OUTLINE"
              size="MEDIUM"
              disabled={!currentEditorValue}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                runExplainQueryMutation();
              }}
              submitting={explainMutation.isLoading}
              Icon={SparklesIcon}
              tooltip="Explain Query"
            />
          ) : null}
          {setIsFullScreen && isFullScreen !== undefined ? (
            <IconButton
              type="OUTLINE"
              size="MEDIUM"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsFullScreen(!isFullScreen);
              }}
              Icon={isFullScreen ? ArrowsPointingInIcon : ArrowsPointingOutIcon}
              tooltip="Expand Full Screen"
            />
          ) : null}
        </div>
      ) : null}

      {triggerType === WorkflowTriggerType.DATA_VIEW && selectedDataView ? (
        <div className="w-full flex justify-center absolute top-10 z-10">
          <p className="backdrop-blur-sm p-2 w-fit text-gray-500 font-medium text-center">
            Imported from "{selectedDataView.title}" Data View •
            <span
              className="text-primary-regular cursor-pointer ml-1"
              onClick={() => {
                if (!readOnly) {
                  setValue(`${fieldPrefix}dataViewId`, null, { shouldDirty: true });
                  setValue(`${fieldPrefix}workflowTriggerType`, WorkflowTriggerType.RAW_SQL, { shouldDirty: true });
                }
              }}
            >
              Overwrite Query
            </span>
          </p>
        </div>
      ) : null}

      <Controller
        control={control}
        name={`${fieldPrefix}query`}
        rules={{ required: true }}
        render={({ field: props }) => (
          // Surrounding div is needed to focus on error
          <div
            tabIndex={-1}
            ref={props.ref}
            className={`${triggerType === WorkflowTriggerType.DATA_VIEW || editorReadonly ? "grayscale pointer-events-none brightness-[0.98]" : ""}`}
          >
            <SqlEditor
              defaultValue={query ?? ""}
              onChange={props.onChange}
              runQuery={runBaseQueryPreview}
              formatQuery={onFormatQuery}
              databaseFormName={`${fieldPrefix}databaseId`}
              initialHeight={600}
              didMountCallback={(editor) => {
                setEditorInstance(editor);
              }}
              readOnly={editorReadonly}
            />
          </div>
        )}
      />
      <QueryPreviews
        prefix={prefix}
        previewOpen={previewOpen}
        setPreviewOpen={setPreviewOpen}
        readOnly={editorReadonly}
        previewColumnsSelectable={previewColumnsSelectable}
      />
      <div className="px-2 flex ">
        {!disableDataViewImport && dataViews && dataViews.length > 0 ? (
          <button
            className="flex gap-2 text-gray-700 items-center flex-col md:flex-row"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setSidebarOpen(true);
            }}
          >
            <ArrowRightCircleIcon className="w-5 h-5" />
            {"Import Data View"}
          </button>
        ) : null}
        <div className="flex-1 flex justify-end gap-2">
          {connectionData?.databaseType !== ConnectionType.MONGODB ? (
            <Button type="SECONDARY" disabled={!currentEditorValue} onClick={onFormatQuery}>
              Format
            </Button>
          ) : null}
          <Button submitting={previewLoading} disabled={!query || !databaseId} id="preview_signal_button" onClick={runBaseQueryPreview}>
            Run Query
          </Button>
        </div>
      </div>
      <GeneralModal title="Query Explanation" modalVisible={explanationModalVisible} setModalVisible={setExplanationModalVisible}>
        <div className="space-y-2">{explanationMessage?.split(".").map((sentence) => <p key={sentence}>{sentence}</p>)}</div>
      </GeneralModal>
    </>
  );
}
